/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib types
import { ApolloClient } from '@apollo/client';
import { DefaultMantineColor, MantineColorsTuple } from '@mantine/core';
import { MIME_TYPES } from '@mantine/dropzone';
import { IconProps } from '@tabler/icons-react';

// Local types
import {
  Resource,
  User,
  Group,
  ResourceInput,
  Permission,
  WorkspacePermission,
} from '@app-types/blackroom-api';
import { QueriedUser } from '@app/features/User/types';
import { ForwardRefExoticComponent, RefAttributes } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Entities                                  */
/* -------------------------------------------------------------------------- */

export declare const APP_ENTITIES: readonly [
  'workspace',
  'group',
  'user',
  'member',
  'admin',
  'resource',
  'file',
  'folder',
  'permission',
  'item',
];
export type AppEntity = (typeof APP_ENTITIES)[number];

/* ******************************** RESOURCE ******************************** */

export declare const RESOURCE_SCOPES: readonly [
  'my',
  'favorites',
  'recent',
  'redacted',
  'archived',
  'search',
];
export type ResourceScope = (typeof RESOURCE_SCOPES)[number] | undefined;

export declare const RESOURCE_TYPE: readonly ['folder', 'file'];

export type MimeType = (typeof MIME_TYPES)[];

export declare const RESOURCE_ACTIONS: readonly [
  'open',
  'share',
  'download',
  'favorite',
  'unfavorite',
  'copy',
  'cut',
  'move',
  'rename',
  'redact',
  'reprocess',
  'archive',
  'delete',
  'restore',
];
export type ResourceAction = (typeof RESOURCE_ACTIONS)[number];

export declare const UNPLOAD_FILE_BUTTONS: readonly [
  'googleDrive',
  'microsoftOneDrive',
  'dropBox',
];
export type UploadFileButton = (typeof UNPLOAD_FILE_BUTTONS)[number];

export declare const ADD_RESOURCE_BUTTONS: readonly [
  'createFolder',
  'addFolder',
  'addFile',
];
export type addResourceButtons = (typeof ADD_RESOURCE_BUTTONS)[number];

export const enum ResourceUploadStatus {
  InProgress = 'in-progress',
  Done = 'done',
  Pending = 'pending',
  Failed = 'failed',
}

export interface TrackedResource extends Resource {
  uploadId: string;
  uploadStatus: ResourceUploadStatus;
  uploadPath: string;
  uploadDuration?: number;
  parentUploadId?: string; // Used to track which folder a file belongs to for cancellation
}

export interface UploadStub extends TrackedResource {
  file?: File; // undefined for folders
}

export interface UploadStubFile extends UploadStub {
  file: File;
}

export interface UploadedStub extends UploadStub {
  resource?: ResourceInput;
}

export declare const RESOURCE_TABLE_NAVIGATIONS: readonly [
  'none',
  'page',
  'table',
];
export type ResourceTableNavigation =
  (typeof RESOURCE_TABLE_NAVIGATIONS)[number];

/* ******************************** REDACTION ******************************* */

export declare const REDACT_TYPES: readonly ['keywords', 'entity'];
export type RedactionMethod = (typeof REDACT_TYPES)[number];

/* ******************************* PERMISSION ******************************* */

export type PermissionKey = keyof Omit<WorkspacePermission, '__typename'>;

export enum PermissionWithNone {
  ResourceAccessOriginal = 'Resource_AccessOriginal',
  ResourceAccessRedacted = 'Resource_AccessRedacted',
  ResourceDownload = 'Resource_Download',
  ResourceNone = 'Resource_None',
}

export enum ResourceStorageVariantEnum {
  original = 'original',
  redacted = 'redacted',
}

export const ResourceStorageVariantPermissionMap: Record<
  ValueOf<typeof ResourceStorageVariantEnum>,
  Permission
> = {
  [ResourceStorageVariantEnum.redacted]: Permission.ResourceAccessRedacted,
  [ResourceStorageVariantEnum.original]: Permission.ResourceAccessOriginal,
};

export declare const ACTION_ROLES: readonly [
  'workspace-admin',
  'tenant-admin',
  'user',
  'workspace-editor',
];
export type ActionRole = (typeof ACTION_ROLES)[number];

/* ********************************** USER ********************************** */

export declare const USER_ACCOUNT_STATUSES: readonly [
  'active',
  'paused',
  'deactivated',
];
export type UserAccountStatus = (typeof USER_ACCOUNT_STATUSES)[number];

export declare const USER_ACTIVITY_STATUSES: readonly [
  'online',
  'idle',
  'offline',
];
export type UserActivityStatus = (typeof USER_ACTIVITY_STATUSES)[number];

export declare const USER_MENU_STATES: readonly ['closed', 'open'];
export type UserMenuState = (typeof USER_MENU_STATES)[number];

export declare const USER_ACTIONS: readonly [
  'open',
  'manageGroups',
  'resendInvitation',
  'suspend',
  'unsuspend',
  'viewAs',
];
export type UserAction = (typeof USER_ACTIONS)[number];

/* ***************************** AG GRID EXTRAS ***************************** */

export type RowResource = Resource;

export type RowUser = QueriedUser;

export type RowGroup = Pick<Group, 'id' | 'name' | 'color' | 'folderId'>;

export type RowGroups = RowGroup[];

export interface RowUserProfile extends Pick<
  User,
  'id' | 'firstName' | 'lastName' | 'workspaceUsers'
> {}

/* ******************************** SHARED UI ******************************* */

export interface StoreSetArgs {
  apolloClient: ApolloClient<object>;
}

export interface StoreArgs {
  cacheKey?: string;
}

export type IconType = ForwardRefExoticComponent<
  IconProps & RefAttributes<SVGSVGElement>
>;

type ExtendedCustomColors =
  | 'blackroomWhite'
  | 'blackroomBlue'
  | 'blackroomGreen'
  | 'blackroomOrange'
  | 'blackroomRed'
  | 'blackroomGrey'
  | 'blackroomBlack'
  | 'blackroomYellow'
  | DefaultMantineColor;

declare module '@mantine/core' {
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, MantineColorsTuple>;
  }
}

/* ********************************** MISC ********************************** */

type ValueOf<T> = T[keyof T];

/* -------------------------------------------------------------------------- */
/*                                    Style                                   */
/* -------------------------------------------------------------------------- */

/**
 * A message conveyed by a widget color weight
 */
export declare const COLOR_MESSAGES: readonly [
  'default',
  'danger',
  'warning',
  'success',
  'neutral',
];
export type ColorMessage = (typeof COLOR_MESSAGES)[number];
